import React from 'react';
import Card from './Card';

export interface Column<T> {
    key: string;
    title: string;
    dataIndex?: keyof T;
    render?: (value: any, record: T, index: number) => React.ReactNode;
    width?: string;
    align?: 'left' | 'center' | 'right';
    sortable?: boolean;
    hidden?: boolean;
}

interface TableProps<T> {
    data: T[];
    columns: Column<T>[];
    loading?: boolean;
    emptyText?: string;
    className?: string;
    glassy?: boolean;
    variant?: 'standard' | 'ledger';
    onRowClick?: (record: T, index: number) => void;
}

const Table = <T extends Record<string, any>>({
    data,
    columns,
    loading = false,
    emptyText = 'No data available',
    className = '',
    glassy = false,
    variant = 'standard',
    onRowClick
}: TableProps<T>) => {
    if (loading) {
        return (
            <Card glassy={glassy} className={`overflow-hidden ${className}`}>
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
            </Card>
        );
    }

    const isLedger = variant === 'ledger';
    const visibleColumns = columns.filter(col => !col.hidden);

    return (
        <Card glassy={glassy} className={`overflow-hidden border-none shadow-sm ${className}`} padding="none">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                    <thead className={isLedger ? 'bg-[#F5F3FF]' : 'bg-gray-50'}>
                        <tr>
                            {visibleColumns.map((column, idx) => (
                                <th
                                    key={column.key}
                                    className={`px-6 py-4 text-${column.align || 'left'} text-[11px] font-bold tracking-widest uppercase
                                        ${isLedger ? 'text-[#5B21B6] border-r border-gray-200/50 last:border-r-0' : 'text-gray-500'}
                                    `}
                                    style={{ width: column.width }}
                                >
                                    {column.title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-50">
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={visibleColumns.length} className="px-6 py-12 text-center text-gray-500">
                                    {emptyText}
                                </td>
                            </tr>
                        ) : (
                            data.map((record, index) => (
                                <tr
                                    key={index}
                                    onClick={() => onRowClick?.(record, index)}
                                    className={`hover:bg-gray-50 transition-colors duration-200 relative ${onRowClick ? 'cursor-pointer' : ''}`}
                                >
                                    {visibleColumns.map((column, colIdx) => (
                                        <td
                                            key={column.key}
                                            className={`px-6 py-4 whitespace-nowrap text-${column.align || 'left'} text-sm
                                                ${isLedger ? 'text-gray-600 font-medium' : 'text-gray-900'}
                                                ${isLedger && colIdx === 0 ? 'border-l-4 border-[#7C3AED]' : ''}
                                            `}
                                        >
                                            {column.render
                                                ? column.render(column.dataIndex ? record[column.dataIndex] : undefined, record, index)
                                                : (column.dataIndex ? (record as any)[column.dataIndex] : null)
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

export default Table;